import React, { useState } from 'react';
import { SiteContent } from '../data/content';

interface PushIncidentFlowProps {
  content: SiteContent['pushIncident'];
}

export const PushIncidentFlow: React.FC<PushIncidentFlowProps> = ({ content }) => {
  const [activeStepIndex, setActiveStepIndex] = useState<number>(0);
  const totalSteps = content.steps.length;
  const currentStep = content.steps[activeStepIndex] || content.steps[0];

  const handlePrev = () => {
    setActiveStepIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setActiveStepIndex((prev) => Math.min(totalSteps - 1, prev + 1));
  };

  return (
    <section className="incident-section" id="incident" aria-labelledby="incident-title">
      <div className="container">
        <div className="section-head">
          <p className="eyebrow">{content.eyebrow}</p>
          <h2 id="incident-title" className="section-title">
            {content.title}
          </h2>
          <p className="section-subtitle">{content.subtitle}</p>
        </div>

        {/* Dramatic Horizontal Phase Rail & Console */}
        <div className="incident-rail-container">
          <div className="incident-rail-topbar">
            <span className="incident-rail-badge">{content.badge}</span>
            <span className="incident-rail-notice">{content.sourceNotice}</span>
          </div>

          <div
            className="incident-phases-track"
            role="tablist"
            aria-label="Incident sequence phases"
          >
            {content.steps.map((step, idx) => {
              const isActive = idx === activeStepIndex;
              return (
                <button
                  key={step.id}
                  type="button"
                  role="tab"
                  id={`incident-tab-${step.id}`}
                  aria-selected={isActive}
                  aria-controls={`incident-panel-${step.id}`}
                  className={`incident-phase-btn ${isActive ? 'is-active' : ''}`}
                  onClick={() => setActiveStepIndex(idx)}
                >
                  <span className="phase-num">{step.step}</span>
                  <span className="phase-name">{step.phase}</span>
                </button>
              );
            })}
          </div>

          {/* Active Phase Detailed Presentation */}
          <div
            id={`incident-panel-${currentStep.id}`}
            role="tabpanel"
            aria-labelledby={`incident-tab-${currentStep.id}`}
            className="incident-stage-card"
          >
            <div className="stage-top-meta">
              <span className="stage-tag">
                STAGE {currentStep.step} / {currentStep.phase}
              </span>
              <span className="stage-boundary">
                {currentStep.context}
              </span>
            </div>

            <h3 className="stage-heading">{currentStep.title}</h3>

            <p className="stage-body">{currentStep.detail}</p>

            <div className="stage-action-box">
              <span className="stage-action-label">SYSTEM ACTION & RESOLUTION:</span>
              <p className="stage-action-text">{currentStep.systemAction}</p>
            </div>

            <div className="stage-controls">
              <button
                type="button"
                className="incident-nav-btn"
                onClick={handlePrev}
                disabled={activeStepIndex === 0}
              >
                {content.navigation.prev}
              </button>

              <span className="incident-step-counter">
                {content.navigation.stepOf(activeStepIndex + 1, totalSteps)}
              </span>

              <button
                type="button"
                className="incident-nav-btn"
                onClick={handleNext}
                disabled={activeStepIndex === totalSteps - 1}
              >
                {content.navigation.next}
              </button>
            </div>
          </div>

          {/* Complete Horizontal Sequence Flow Overview */}
          <div className="incident-sequence-overview">
            <span className="overview-label">STAGE SEQUENCE:</span>
            <div className="overview-steps">
              {content.steps.map((step, idx) => (
                <button
                  key={step.id}
                  type="button"
                  className={`overview-step-chip ${idx === activeStepIndex ? 'is-current' : ''}`}
                  onClick={() => setActiveStepIndex(idx)}
                >
                  <span className="chip-step">{step.step}</span>
                  <span className="chip-phase">{step.phase}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
